import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Calendar, Clock, Utensils } from 'lucide-react';
import axios from 'axios';
import OptimizedImage from '../components/OptimizedImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // Fetch menu items that have images uploaded by owner
        const response = await axios.get(`${API}/menu`);
        // Filter items that have actual uploaded images (not placeholders)
        const itemsWithImages = response.data.filter(item => 
          item.image_url && 
          item.image_url.includes('emergentagent.com')
        );
        setFeaturedItems(itemsWithImages.slice(0, 3));
      } catch (error) {
        console.error('Error fetching featured items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Google Review",
      text: "Absolutely stunning experience! The Black Hummus was unlike anything I've ever tasted - the activated charcoal gives it such a unique depth. The Aged Ribeye was cooked to perfection. We'll definitely be back!",
      rating: 5,
      date: "2 weeks ago"
    },
    {
      name: "David Thompson",
      role: "TripAdvisor",
      text: "Celebrated our anniversary here and couldn't have picked a better place. The Lamb Shank Risotto melted in my mouth, and the Black Garlic Naan is simply addictive. Service was impeccable throughout.",
      rating: 5,
      date: "1 month ago"
    },
    {
      name: "Priya Sharma",
      role: "Google Review",
      text: "The Crispy Ceviche with Scottish trout is a must-try! Fresh, delicate, and beautifully presented. The ambiance is elegant without being stuffy. Perfect for both business dinners and special occasions.",
      rating: 5,
      date: "3 weeks ago"
    },
    {
      name: "Michael Chen",
      role: "OpenTable",
      text: "Visited with colleagues and everyone was impressed. The Short Ribs with Silky Mash is incredible - fall-off-the-bone tender. Great wine selection too. Already booked our next visit!",
      rating: 5,
      date: "1 week ago"
    },
    {
      name: "Emma Williams",
      role: "TripAdvisor",
      text: "Finally a restaurant that lives up to the hype! The Grilled Giant Prawns were succulent and perfectly spiced. Finished with the Kajar Tart - absolutely divine. Worth every penny.",
      rating: 5,
      date: "2 weeks ago"
    },
    {
      name: "James Anderson",
      role: "Google Review",
      text: "Outstanding from start to finish. The Chicken Roulade Makhani was the star of the evening - creamy, flavourful, and beautifully plated. The staff made us feel like VIPs. Highly recommend!",
      rating: 5,
      date: "3 days ago"
    }
  ];

  const dishImages = [
    "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&q=80",
    "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=600&q=80",
    "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=600&q=80"
  ];

  // Get image for featured item - use actual uploaded image or fallback
  const getItemImage = (item, index) => {
    if (item.image_url && item.image_url.includes('emergentagent.com')) {
      return item.image_url;
    }
    return dishImages[index % dishImages.length];
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="https://images.unsplash.com/photo-1687648451534-d9155e6258e4"
            alt="Elegant fine dining restaurant interior"
            className="w-full h-full"
            width={1920}
            quality={75}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-800/60 to-gray-700/40" />
        </div>
        
        <div className="relative z-10 container-elegant">
          <div className="max-w-2xl">
            <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-6 opacity-0 animate-fade-in">
              Fine Dining Experience
            </p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-6 opacity-0 animate-fade-in animation-delay-100">
              Reva <span className="text-malva-light">Dining</span>
            </h1>
            <p className="text-gray-200 font-sans text-lg md:text-xl max-w-xl mb-10 opacity-0 animate-fade-in animation-delay-200">
              Where culinary artistry meets timeless elegance. An unforgettable journey through exceptional flavours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-300">
              <Link 
                to="/booking" 
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="hero-reserve-btn"
              >
                <Calendar className="w-5 h-5" />
                Reserve a Table
              </Link>
              <Link 
                to="/menu" 
                className="btn-secondary-light inline-flex items-center justify-center gap-2"
                data-testid="hero-menu-btn"
              >
                <Utensils className="w-5 h-5" />
                View Menu
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Dishes */}
      <section className="section-padding bg-gradient-to-b from-white to-silver-bright">
        <div className="container-elegant">
          <div className="text-center mb-16">
            <p className="text-malva-dark font-sans text-sm tracking-[0.3em] uppercase mb-4">
              Culinary Excellence
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-800 mb-6">
              Signature Creations
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Each dish tells a story of passion, precision, and the finest ingredients sourced from around the world.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <div key={i} className="card-elegant animate-pulse">
                  <div className="aspect-[4/3] bg-gray-100" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-gray-100 rounded w-3/4" />
                    <div className="h-4 bg-gray-100 rounded w-full" />
                  </div>
                </div>
              ))
            ) : featuredItems.length === 0 ? (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500">Featured dishes coming soon...</p>
              </div>
            ) : (
              featuredItems.map((item, index) => (
                <div 
                  key={item.id} 
                  className={`card-elegant group opacity-0 animate-fade-in animation-delay-${(index + 1) * 100}`}
                  data-testid={`featured-dish-${index}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={getItemImage(item, index)}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 bg-gradient-to-b from-white to-silver-bright/50">
                    <p className="text-malva text-xs font-sans tracking-widest uppercase mb-2">
                      {item.category}
                    </p>
                    <h3 className="font-serif text-xl text-gray-800 mb-2">
                      {item.name}
                    </h3>
                    <p className="text-gray-500 text-sm line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/menu" 
              className="inline-flex items-center gap-2 text-malva-dark hover:text-malva-darker transition-colors duration-300 font-sans tracking-wide"
              data-testid="view-full-menu-link"
            >
              View Full Menu
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-malva-darker relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
        </div>
        <div className="container-elegant relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-xl overflow-hidden shadow-2xl border-4 border-malva-light/30">
                <img
                  src="https://images.unsplash.com/photo-1559058789-672da06263d8?w=800&q=80"
                  alt="Chef preparing signature dish"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-malva-light/30 rounded-xl -z-10" />
            </div>
            
            <div>
              <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-4">
                Our Story
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">
                A Legacy of Excellence
              </h2>
              <p className="text-white/80 mb-6 leading-relaxed">
                At Reva Dining, we craft unforgettable culinary experiences. From our signature <strong>Black Hummus</strong> with premium Lebanese tahini to the succulent <strong>45-day Aged Ribeye Steak</strong>, every dish is a masterpiece of flavour and technique.
              </p>
              <p className="text-white/60 mb-8 leading-relaxed">
                Our menu celebrates bold flavours—the caramelised sweetness of our <strong>Black Garlic Naan</strong>, the delicate freshness of <strong>Crispy Ceviche</strong>, and the rich indulgence of <strong>Lamb Shank Risotto</strong>. This is fine dining reimagined.
              </p>
              <Link 
                to="/about" 
                className="btn-secondary-light inline-flex items-center gap-2"
                data-testid="learn-more-btn"
              >
                Learn More
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Stats */}
      <section className="py-20 bg-gradient-to-b from-silver-bright to-white border-y border-malva/20">
        <div className="container-elegant">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "15", label: "Years of Excellence", suffix: "+" },
              { number: "2", label: "Michelin Stars", suffix: "" },
              { number: "50k", label: "Happy Guests", suffix: "+" },
              { number: "4.9", label: "Average Rating", suffix: "/5" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-serif text-4xl md:text-5xl text-gray-800 mb-2">
                  {stat.number}<span className="text-malva">{stat.suffix}</span>
                </p>
                <p className="text-gray-500 text-sm font-sans tracking-wider uppercase">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gradient-to-b from-white to-silver-bright">
        <div className="container-elegant">
          <div className="text-center mb-16">
            <p className="text-malva-dark font-sans text-sm tracking-[0.3em] uppercase mb-4">
              Guest Reviews
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-800 mb-4">
              What Our Guests Say
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Real reviews from our valued guests, sharing their dining experiences at Reva Dining.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.name} 
                className="card-elegant p-6 bg-gradient-to-b from-white to-malva/5 hover:shadow-xl transition-all duration-300"
                data-testid={`testimonial-${testimonial.name.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1">
                    {Array(testimonial.rating).fill(0).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                    {testimonial.date}
                  </span>
                </div>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-malva/10">
                  <div className="w-10 h-10 rounded-full bg-malva/20 flex items-center justify-center">
                    <span className="text-malva-dark font-serif text-sm font-medium">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium text-sm">{testimonial.name}</p>
                    <p className="text-gray-400 text-xs">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80"
            alt="Restaurant ambiance"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-malva-darker/90 via-malva-dark/80 to-malva/70" />
        </div>
        
        <div className="relative z-10 container-elegant text-center">
          <p className="text-malva-light font-sans text-sm tracking-[0.3em] uppercase mb-4">
            Experience Excellence
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
            Reserve Your Table
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-10">
            Join us for an evening of exceptional cuisine and impeccable service. Create memories that will last a lifetime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/booking" 
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="cta-reserve-btn"
            >
              <Calendar className="w-5 h-5" />
              Make Reservation
            </Link>
            <Link 
              to="/order" 
              className="btn-secondary-light inline-flex items-center justify-center gap-2"
              data-testid="cta-order-btn"
            >
              <Clock className="w-5 h-5" />
              Order for Collection
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
